import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Alert, Layout, List, Card, Tooltip } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { buildPageHeaderBreadcrumb } from '../../updated/utils'; 
import { Routes } from '../../routes'; 
import { STATS_QUERY } from '../../gql/queries';
import './styles.less';
import SEARCH_QUERY from '../../updated/gql/queries/SEARCH_QUERY';
import { CompanyContext } from '../../providers/companyProvider';

const StatsPage = () => {

    const { data, loading } = useQuery( STATS_QUERY, {  fetchPolicy: 'network-only' } );

    const statsData = useMemo( () => {
        if ( !loading && data ) {
            return data.stats;
        }

        return null;
    }, [ data, loading ] );

    return (
        <div className="stats-page">
            <Layout>
                <PageHeader     
                    breadcrumb={ buildPageHeaderBreadcrumb( [
                        { breadcrumbName: 'Course Studio', path: Routes.Home.buildPath() },
                        { breadcrumbName: 'Analytics', path: Routes.Reporting.buildPath() },
                        { breadcrumbName: 'Stats' }
                    ] ) } 
                />

                <CompanyDetails stats={ statsData } loading={ loading } />

                <LearningObjectsDetails stats={ statsData } loading={ loading } />

                <CourseDetails stats={ statsData } loading={ loading } />

                <CurriculumDetails stats={ statsData } loading={ loading } />

                <EnrollmentDetails stats={ statsData } loading={ loading } />

                <AssignmentDetails stats={ statsData } loading={ loading } />

                <AssetDetails stats={ statsData } loading={ loading } />
                
            </Layout>
        </div>
    );
};

const LearningObjectsDetails = ( { stats, loading } ) => {

    const { data: searchData, loading: searchLoading } = useQuery( SEARCH_QUERY, { variables: { limit: 0, types: [ 'objects' ] } } );

    const data = useMemo( () => {
        if ( !stats?.learningObjects ) { return []; }
        return [
            {
                name: 'Active',
                value: stats.learningObjects.active
            },
            {
                name: 'Deleted',
                value: stats.learningObjects.inactive
            },
            {
                name: 'Total',
                value: stats.learningObjects.total
            },
        ];
    
    }, [ stats ] );

    const extra = useMemo( () => {
        if ( !searchData?.search?.edges || searchLoading || !stats?.learningObjects ) {
            return undefined;
        }

        const total = searchData.search.totalCount;

        return <Alert message={ `${ total } returned by search query` } type={ total == stats.learningObjects.active ? 'success' : 'warning' } />;

    }, [ searchData, stats ] );

    return <DetailCard title='Learning Objects' loading={ loading } data={ data } extra={ extra } />;
};

const CourseDetails = ( { stats, loading } ) => {

    const { data: searchData, loading: searchLoading } = useQuery( SEARCH_QUERY, { variables: { limit: 0, types: [ 'courses' ] } } );

    const data = useMemo( () => {
        if ( !stats?.courses ) { return []; }
        return [
            {
                name: 'Active',
                value: stats.courses.active
            },
            {
                name: 'Deleted',
                value: stats.courses.inactive
            },
            {
                name: 'Total',
                value: stats.courses.total
            },
        ];
    
    }, [ stats ] );

    const extra = useMemo( () => {
        if ( !searchData?.search?.edges || searchLoading || !stats?.courses ) {
            return undefined;
        }

        const total = searchData.search.totalCount;

        return <Alert message={ `${ total } returned by search query` } type={ total == stats.courses.active ? 'success' : 'warning' } />;

    }, [ searchData, stats ] );

    return <DetailCard title='Courses' loading={ loading } data={ data } extra={ extra } />;
};

const CurriculumDetails = ( { stats, loading } ) => {

    const { data: searchData, loading: searchLoading } = useQuery( SEARCH_QUERY, { variables: { limit: 0, types: [ 'curricula' ] } } );

    const data = useMemo( () => {
        if ( !stats?.curricula ) { return []; }
        return [
            {
                name: 'Total',
                value: stats.curricula.total
            },
        ];
    
    }, [ stats ] );

    const extra = useMemo( () => {
        if ( !searchData?.search?.edges || searchLoading || !stats?.curricula ) {
            return undefined;
        }

        const total = searchData.search.totalCount;

        return <Alert message={ `${ total } returned by search query` } type={ total == stats.curricula.total ? 'success' : 'warning' } />;

    }, [ searchData, stats ] );

    return <DetailCard title='Curriculums' loading={ loading } data={ data } extra={ extra } />;
};

const EnrollmentDetails = ( { stats, loading } ) => {

    const data = useMemo( () => {
        if ( !stats?.enrollments ) { return []; }
        return [
            {
                name: 'Assigned',
                value: stats.enrollments.assigned
            },
            {
                name: 'In Progress',
                value: stats.enrollments.inProgress
            },
            {
                name: 'Completed',
                value: stats.enrollments.completed
            },
            {
                name: 'Total',
                value: stats.enrollments.total
            },
        ];
    
    }, [ stats ] );

    return <DetailCard title='Course Enrollments' loading={ loading } data={ data } />;
};

const AssignmentDetails = ( { stats, loading } ) => {

    const data = useMemo( () => {
        if ( !stats?.assignments ) { return []; }
        return [
            {
                name: 'Assigned',
                value: stats.assignments.assigned
            },
            {
                name: 'In Progress',
                value: stats.assignments.inProgress
            },
            {
                name: 'Completed',
                value: stats.assignments.completed
            },
            {
                name: 'Total',
                value: stats.assignments.total
            },
        ];
    
    }, [ stats ] );

    return <DetailCard title='Assignments' extra={
        <Tooltip title="The count of user statuses in all learning objects on the learning site. 
                            Assigned objects occur when an In Progress course contains multiple objects and 
                            some have not been started. The objects in an In Progress course that have been 
                            started or finished will be counted in In Progress or Completed, respectively." >
        <QuestionCircleOutlined /></Tooltip>} loading={ loading } data={ data } />;
};

const AssetDetails = ( { stats, loading } ) => {

    const data = useMemo( () => {
        if ( !stats?.assets ) { return []; }
        return [
            {
                name: 'Total',
                value: stats.assets.total
            },
        ];
    
    }, [ stats ] );

    return <DetailCard title='Assets' loading={ loading } data={ data } />;
};

const CompanyDetails = ( { stats, loading } ) => {

    const { company } = useContext( CompanyContext );


    useEffect( () => {
        if ( !stats?.companyId ) {return;}
    }, [ stats ] );

    const details = useMemo( () => {
        if ( stats ) {
            return [
                {
                    name: 'Company Name',
                    value: company?.name
                },
                {
                    name: 'UUID',
                    value: stats.companyId
                },
                {
                    name: 'Users with Engagement',
                    value: stats.users
                },
                {
                    name: 'Total Users',
                    value: stats.totalUsers
                }
            ];
        }
    }, [ stats, company ] );

    return <DetailCard title='Company' extra={ <img src={ company?.primaryLogo ?? '' } /> } loading={ loading } data={ details } />;
};

const DetailCard = ( { data, extra, title, loading } ) => {

    const formatter = new Intl.NumberFormat( 'en-US' );

    return <Card title={ title } extra={ extra }>
        <List
            size="small"
            itemLayout="horizontal"
            loading={ loading }
            dataSource={ data }
            renderItem={ ( item ) => <List.Item>
                <List.Item.Meta
                    title={ item.name } />
                <div>{ isNaN( item.value ) ? item.value : formatter.format( item.value ) }</div>
            </List.Item> 
            } 
        />
    </Card>;
};


export default StatsPage;
